/** Port of app/lib/mortgage.py. */

import type { MortgageDetail } from './mortgage_detail.ts';

export class UnendingMortgageError extends Error {}
export class UnaffordableMortgageError extends Error {}

export interface Overpayment {
    month_index: number;
    amount: number;
}

export interface Payment {
    remaining_debt: number;
    annual_interest_rate: number;
    interest_charged: number;
    month_count: number;
    charge: number;
}

export interface Mortgage {
    mortgage_detail: MortgageDetail;
    payments: Payment[];
    principle: number;
}

/**
 * Matches `statistics.mode`: the most common value, and on a tie the one that
 * appeared first. `Mortgage.monthlyCost` depends on this — the final part-month
 * payment is smaller than the rest, so mean or first would both be wrong.
 */
function mode(values: number[]) {
    const counts = new Map<number, number>();
    let best = values[0];
    let best_count = 0;

    for (const value of values) {
        const count = (counts.get(value) ?? 0) + 1;
        counts.set(value, count);
        if (count > best_count) {
            best_count = count;
            best = value;
        }
    }

    return best;
}

export function length(mortgage: Mortgage) {
    return mortgage.payments.filter((payment) => payment.charge !== 0).length;
}

export function monthlyCost(mortgage: Mortgage) {
    if (mortgage.payments.length === 0) {
        return 0;
    }
    return mode(mortgage.payments.map((payment) => payment.charge));
}

export function totalCost(mortgage: Mortgage) {
    return mortgage.payments.reduce(
        (total, payment) => total + payment.charge,
        0,
    );
}

export function totalInterest(mortgage: Mortgage) {
    return totalCost(mortgage) - mortgage.principle;
}

/**
 * One month of the amortisation. Every other function here composes this.
 *
 * A fixed term covers exactly `fixed_term * 12` payments: month_index counts
 * from 0, so a 2 year fix is months 0 to 23 and the variable rate starts on the
 * 25th. The Python this was ported from used `<=` and so charged the fixed rate
 * for one payment too many — 25 payments on a 2 year fix, with the switch
 * landing a month inside the year after. That went unnoticed until the schedule
 * put the months on screen, and correcting it moved every figure the app
 * reports: on £100,000 at 4.65% fixed and 6.99% after, £107 more interest over
 * 10 years, £244 over 25 and £320 over 35.
 */
export function nextPayment(
    month_index: number,
    amount_paid: number,
    balance: number,
    mortgage_detail: MortgageDetail,
): Payment {
    const interest_rate =
        month_index < mortgage_detail.fixed_term * 12
            ? mortgage_detail.initial_interest
            : mortgage_detail.subsequent_interest;
    const interest_charged = balance * interest_rate * (1 / 12);
    const charge = Math.min(balance + interest_charged, amount_paid);
    const remaining_debt = balance + interest_charged - charge;

    return {
        annual_interest_rate: interest_rate,
        interest_charged,
        month_count: month_index,
        remaining_debt,
        charge,
    };
}

export function forMonthlyCost(
    mortgage_detail: MortgageDetail,
    monthly_charge: number,
    principle: number,
): Mortgage {
    const payments: Payment[] = [];
    let outstanding_balance = principle;
    let month_index = 0;

    while (outstanding_balance > 0) {
        if (month_index > 100 * 12) {
            throw new UnendingMortgageError(
                `Even after 100 years at £${formatAmount(monthly_charge)}/month, ` +
                    `principle £${formatAmount(principle)} won't be paid off.`,
            );
        }

        const payment = nextPayment(
            month_index,
            monthly_charge,
            outstanding_balance,
            mortgage_detail,
        );
        payments.push(payment);
        outstanding_balance = payment.remaining_debt;
        month_index = month_index + 1;
    }

    return { mortgage_detail, payments, principle };
}

const MAX_MONTHLY_CHARGE = 10000;

/** Runs `total_months` of amortisation at a fixed charge. */
function amortise(
    mortgage_detail: MortgageDetail,
    total_months: number,
    principle: number,
    monthly_charge: number,
) {
    const payments: Payment[] = [];
    let outstanding_balance = principle;

    for (let month_index = 0; month_index < total_months; month_index++) {
        const payment = nextPayment(
            month_index,
            monthly_charge,
            outstanding_balance,
            mortgage_detail,
        );
        payments.push(payment);
        outstanding_balance = payment.remaining_debt;
    }

    return { payments, outstanding_balance };
}

/**
 * Inverse of forMonthlyCost: the smallest whole number of pence per month that
 * clears `principle` in exactly `total_months`. A search rather than a closed
 * form because the rate changes partway through, which the amortisation formula
 * doesn't model.
 *
 * Bisection, over pence, on the balance left at `total_months` — which only
 * falls as the charge rises, and is clamped at zero because a payment never
 * exceeds the debt. That clamp is why this looks for the *smallest* charge that
 * lands on zero: every larger one lands there too, having paid the mortgage off
 * early. An earlier version scanned upwards in £10 steps and took the first hit,
 * which at long terms was worth a couple of years of term — 33 and 34 years both
 * came out at £590/month and identical interest, both of them really the cost of
 * a 32.3 year mortgage.
 */
export function findMortgage(
    mortgage_detail: MortgageDetail,
    total_months: number,
    principle: number,
): Mortgage {
    const clears = (pence: number) =>
        amortise(mortgage_detail, total_months, principle, pence / 100)
            .outstanding_balance === 0;

    let high = MAX_MONTHLY_CHARGE * 100;
    if (!clears(high)) {
        throw new UnaffordableMortgageError(
            `No mortgage found for less than £${MAX_MONTHLY_CHARGE.toFixed(2)}/month`,
        );
    }

    // Invariant: `low` never clears the debt, `high` always does. Nothing left
    // to borrow is the one case where paying nothing clears it, and it would
    // break that.
    if (clears(0)) {
        high = 0;
    }

    let low = 0;
    while (high - low > 1) {
        const middle = Math.floor((low + high) / 2);
        if (clears(middle)) {
            high = middle;
        } else {
            low = middle;
        }
    }

    const { payments } = amortise(
        mortgage_detail,
        total_months,
        principle,
        high / 100,
    );

    return { mortgage_detail, payments, principle };
}

export function applyOverpayments(
    mortgage: Mortgage,
    overpayments: Overpayment[],
): Mortgage {
    const by_month = new Map(
        overpayments.map((overpayment) => [
            overpayment.month_index,
            overpayment.amount,
        ]),
    );

    const payments: Payment[] = [];
    let outstanding_balance = mortgage.principle;
    const monthly = monthlyCost(mortgage);

    for (let month_index = 0; month_index < length(mortgage); month_index++) {
        const payment = nextPayment(
            month_index,
            monthly + (by_month.get(month_index) ?? 0),
            outstanding_balance,
            mortgage.mortgage_detail,
        );
        payments.push(payment);
        outstanding_balance = payment.remaining_debt;
    }

    return {
        mortgage_detail: mortgage.mortgage_detail,
        payments,
        principle: mortgage.principle,
    };
}

/** Python's `{:,.0f}`. */
function formatAmount(value: number) {
    return Math.round(value).toLocaleString('en-US');
}

/** Mirrors the Python `__repr__`, which the ported test asserts on verbatim. */
export function repr(mortgage: Mortgage) {
    const charges = mortgage.payments
        .map((payment) => Math.round(payment.charge))
        .join(', ');

    return (
        `Mortgage: £${formatAmount(mortgage.principle)}\n` +
        `${length(mortgage)} months\n` +
        `£${formatAmount(totalCost(mortgage))} total cost\n` +
        `£${formatAmount(totalInterest(mortgage))} in interest\n` +
        `£${formatAmount(monthlyCost(mortgage))} monthly\n` +
        `[${charges}]\n`
    );
}
